/*
 * ॐ नमः शिवाय
 * ಓಂ ನಮಃ ಶಿವಾಯ
 * ఓం నమః శివాయ
 * ஓம் நம சிவாய
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms 
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this 
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and 
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

/**
 * TableListPage Component
 * -----------------------
 * This component renders a paginated list of tables for an application
 * with server-side search and pagination support.
 *
 * Features:
 * 1. Initial load:
 *    - Fetches all tables from backend for application with pagination.
 * 2. Search functionality:
 *    - Calls backend API with search query and pagination.
 *    - Resets page to 1 on search change.
 * 3. Pagination:
 *    - Server side pagination controlled by page state.
 *    - Shows page count and navigation buttons.
 * 4. Responsive UI (Dual Layout based on container width):
 *    - Desktop (containerWidth > 991px): Table with sticky actions column and
 *      horizontally scrollable container.
 *    - Mobile (containerWidth ≤ 991px): Stacked card layout with vertically
 *      arranged data.
 * 5. Theming:
 *    - Uses ThemeContext for colors, borders, and fonts.
 * 6. Error and Loading handling:
 *    - Shows loading spinner during fetch.
 *    - Shows errors if fetch fails.
 * 7. UX:
 *    - Search input debounced by React state update.
 *    - Pagination updates fetch dynamically.
 */

import React, { useEffect, useRef, useState } from "react";
import { useTheme } from "../theme/ThemeContext";
import Paginator from "../components/common/Paginator";
import APIHelper from "../api/APIHelper";
import envConfig from "../config/envConfig";
import { useNavigate } from "react-router-dom";
import { handleErrorResponse } from "../utils/utils";
import { useQuery, useQueryClient } from "@tanstack/react-query";

const apiHelper = new APIHelper({
  apiUrl: envConfig.backendApiUrl,
  tokenUrl: envConfig.backendTokenUrl,
  clientId: envConfig.clientKey,
  clientSecret: envConfig.clientSecret,
});

const applicationId = envConfig.applicationId;
const perPage = 20;

/**
 * Debounce hook for search value.
 * Keeps "search" responsive but reduces backend calls.
 */
function useDebounce(value, delay = 350) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

export default function TableListPage() {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const queryClient = useQueryClient();

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 350);

  /**
   * Container width tracking:
   * - We measure the actual rendered width of the outer `.container` using a ref.
   * - This avoids confusion when Bootstrap max-width makes the container smaller
   *   than window.innerWidth.
   */
  const containerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);

  /**
   * Helper: log both window and container widths on resize.
   * Useful for debugging responsive behavior.
   */
  const logWidths = (winWidth, contWidth) => {
    console.log("Window innerWidth:", winWidth, "Container width:", contWidth);
  };

  useEffect(() => {
    function handleResize() {
      const winWidth = typeof window !== "undefined" ? window.innerWidth : 0;
      let contWidth = 0;
      if (containerRef.current) {
        contWidth = containerRef.current.offsetWidth;
      }
      setContainerWidth(winWidth);
      logWidths(winWidth, contWidth);
    }

    // Initial measurement after mount
    handleResize();

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Responsive flags derived from actual container width
  const isMobile = containerWidth <= 991;
  const isVerySmallScreen = containerWidth < 400;

  const queryKey = ["tables", applicationId, debouncedSearch.trim(), page, perPage];

  /**
   * Fetch tables list with optional search and pagination.
   */
  const fetchTables = async ({ signal }) => {
    const searchParam = debouncedSearch.trim()
      ? `&search=${encodeURIComponent(debouncedSearch.trim())}`
      : "";
    const url = `/arcee/application/table/list/?application_id=${applicationId}${searchParam}&page=${page}&page_size=${perPage}`;

    const response = await apiHelper.apiCall(url, { method: "GET", signal });

    if (!response.ok) {
      await handleErrorResponse(response);
      throw new Error("Failed to fetch tables");
    }
    const data = await response.json();
    return data;
  };

  const {
    data,
    error,
    isLoading,
    isFetching,
  } = useQuery({
    queryKey,
    queryFn: fetchTables,
    keepPreviousData: true,
    staleTime: envConfig.staleTimeMs,
    retry: 1,
  });

  /**
   * Reset page to 1 whenever debounced search changes.
   *
   * MOVED INTO THE HANDLER. As an effect this fired AFTER the query key had
   * already changed, so searching from page 3 sent two requests: one for
   * {newSearch, page 3}, then another for {newSearch, page 1} once this ran.
   * Resetting alongside the search text means the key changes once and only the
   * page the user will actually see is fetched.
   *
   * useEffect(() => {
   *   setPage(1);
   * }, [debouncedSearch]);
   */

  const handleSearchChange = (e) => {
    const val = e.target.value.slice(0, 128);
    setSearch(val);
    setPage(1);
  };

  const handlePageChange = (newPage) => setPage(newPage);
  const handlePrevious = () => setPage((p) => Math.max(p - 1, 1));
  const handleNext = () => {
    const pageCount = data ? Math.max(1, Math.ceil((data.count || 0) / perPage)) : 1;
    setPage((p) => Math.min(p + 1, pageCount));
  };

  /**
   * Row action handler; currently supports "view".
   */
  async function handleRowAction(action, table) {
    if (action === "view") {
      navigate(`/dashboard/tables/${encodeURIComponent(table.name)}`);
    }
  }

  const pageCount = data ? Math.max(1, Math.ceil((data.count || 0) / perPage)) : 1;
  const tables = data?.results || [];

  // Sticky actions column style (desktop layout)
  const actionsColStyle = {
    position: "sticky",
    right: 0,
    background: "#fff",
    zIndex: 10,
    boxShadow: "-2px 0px 5px rgba(0,0,0,0.1)",
  };

  // Mobile card styles (for stacked layout)
  const mobileCardStyle = {
    background: "#fff",
    borderRadius: theme.cardRadius,
    boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
    padding: "1em 1.2em",
    marginBottom: "1em",
    border: "1px solid #f0f0f0",
  };

  const mobileLabelStyle = {
    fontWeight: 600,
    color: theme.primary,
    fontSize: "13px",
    marginBottom: "2px",
    textTransform: "uppercase",
    letterSpacing: 0.3,
  };

  const mobileValueStyle = {
    color: "#555",
    fontSize: "14px",
    marginBottom: "10px",
  };

  return (
    <div
      ref={containerRef}
      className="container"
      style={{
        maxWidth: "100%",
        margin: "36px auto",
        fontFamily: theme.fontFamily,
      }}
    >
      {/* Search Bar Input */}
      <div className="row align-items-center mb-2 gx-2">
        <div className="col-12">
          <input
            type="text"
            value={search}
            onChange={handleSearchChange}
            placeholder="Search tables by display name or name"
            className="form-control"
            style={{
              fontSize: isVerySmallScreen ? 12 : 16,
              width: "100%",
              border: `1.1px solid ${theme.primary}`,
              borderRadius: theme.buttonRadius,
              background: "#fafbfc",
            }}
            maxLength={128}
          />
        </div>
      </div>

      {/* MOBILE LAYOUT: Stacked Cards (containerWidth ≤ 991px) */}
      {isMobile && (
        <div
          style={{
            border: `1.2px solid ${theme.primary}`,
            background: "#fff",
            borderRadius: 6,
            padding: 8,
            boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
          }}
        >
          {isLoading || isFetching ? (
            <div
              className="text-center"
              style={{ color: theme.primary, padding: "32px 0" }}
            >
              <i
                className="bi bi-arrow-repeat animate-spin"
                style={{ fontSize: "18px" }}
              ></i>{" "}
              Loading tables...
            </div>
          ) : error ? (
            <div
              className="text-center"
              style={{ color: "red", padding: "32px 0" }}
            >
              {error?.message || "Failed to load"}
            </div>
          ) : tables.length === 0 ? (
            <div
              className="text-center"
              style={{
                color: "#aaa",
                fontStyle: "italic",
                padding: "28px 0",
              }}
            >
              No tables found.
            </div>
          ) : (
            tables.map((t, idx) => (
              <div key={t.name} style={mobileCardStyle}>
                {/* Top row: S.No and Actions icon */}
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: 6,
                  }}
                >
                  <div
                    style={{
                      fontSize: 13,
                      color: "#6c6f85",
                      fontWeight: 600,
                    }}
                  >
                    S.No:&nbsp;
                    <span style={{ color: theme.primary }}>
                      {(page - 1) * perPage + idx + 1}
                    </span>
                  </div>
                  <div>
                    <i
                      className="bi bi-arrow-right-circle"
                      title="View"
                      style={{
                        cursor: "pointer",
                        color: theme.primary,
                        fontSize: 20,
                        padding: 4,
                        borderRadius: 8,
                        transition: "background .15s",
                      }}
                      onClick={() => handleRowAction("view", t)}
                    />
                  </div>
                </div>

                {/* Table Name */}
                <div style={{ marginBottom: 6 }}>
                  <div style={mobileLabelStyle}>Table Name</div>
                  <div
                    style={{
                      ...mobileValueStyle,
                      fontFamily: "monospace",
                      color: "#34374f",
                    }}
                  >
                    {t.name}
                  </div>
                </div>

                {/* Display Name */}
                <div style={{ marginBottom: 6 }}>
                  <div style={mobileLabelStyle}>Display Name</div>
                  <div
                    style={{
                      ...mobileValueStyle,
                      fontWeight: 500,
                    }}
                  >
                    {t.display_name || "-"}
                  </div>
                </div>

                {/* Description */}
                <div style={{ marginBottom: 6 }}>
                  <div style={mobileLabelStyle}>Description</div>
                  <div style={mobileValueStyle}>{t.description || "-"}</div>
                </div>

                {/* Fields Count */}
                <div>
                  <div style={mobileLabelStyle}>Fields</div>
                  <div
                    style={{
                      ...mobileValueStyle,
                      color: theme.primary,
                      fontWeight: 600,
                    }}
                  >
                    {Math.max(0, t.fields_count - 1)}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* DESKTOP LAYOUT: Table (containerWidth > 991px) */}
      {!isMobile && (
        /**
         * Wrap table container in another div that:
         *  - constrains width to the container (maxWidth: 100%)
         *  - hides horizontal overflow on the page itself.
         * Horizontal scroll is allowed only inside the inner container.
         */
        <div
          className="table-responsive rounded shadow-sm"
          style={{
            border: `1.2px solid ${theme.primary}`,
            background: "#fff",
            overflowX: "auto", // horizontal scroll only inside this box
            maxWidth: "100%",
            padding: 0,
          }}
        >
          <table
            className="table table-striped mb-0 align-middle"
            style={{ minWidth: 800 }} // wider than some viewports so scroll is needed
          >
            <thead>
              <tr
                style={{
                  fontWeight: 600,
                  color: theme.primary,
                  background: "#f7f7fa",
                  fontSize: 16,
                }}
              >
                <th style={{ minWidth: 80, textAlign: "left" }}>S.No</th>
                <th style={{ minWidth: 160, textAlign: "left" }}>Table Name</th>
                <th style={{ minWidth: 200 }}>Display Name</th>
                <th style={{ minWidth: 260 }}>Description</th>
                <th style={{ minWidth: 90 }}>Fields</th>
                <th
                  style={{
                    ...actionsColStyle,
                    minWidth: 110,
                    textAlign: "right",
                  }}
                >
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {isLoading || isFetching ? (
                <tr style={{ height: 150 }}>
                  <td
                    colSpan={6}
                    className="text-center"
                    style={{ color: theme.primary }}
                  >
                    <i
                      className="bi bi-arrow-repeat animate-spin"
                      style={{ fontSize: "18px" }}
                    ></i>{" "}
                    Loading tables...
                  </td>
                </tr>
              ) : error ? (
                <tr style={{ height: 150 }}>
                  <td
                    colSpan={6}
                    className="text-center"
                    style={{ color: "red" }}
                  >
                    {error?.message || "Failed to load"}
                  </td>
                </tr>
              ) : tables.length === 0 ? (
                <tr>
                  <td
                    colSpan={6}
                    className="text-center"
                    style={{
                      color: "#aaa",
                      fontStyle: "italic",
                      padding: "28px 0",
                    }}
                  >
                    No tables found.
                  </td>
                </tr>
              ) : (
                tables.map((t, idx) => (
                  <tr key={t.name}>
                    <td
                      style={{ textAlign: "left", verticalAlign: "middle" }}
                    >
                      {(page - 1) * perPage + idx + 1}
                    </td>
                    <td
                      style={{
                        fontFamily: "monospace",
                        color: "#435",
                        textAlign: "left",
                        verticalAlign: "middle",
                      }}
                    >
                      {t.name}
                    </td>
                    <td className="align-middle fw-medium">
                      {t.display_name || "-"}
                    </td>
                    <td
                      className="align-middle"
                      style={{ color: "#5e6380" }}
                    >
                      {t.description}
                    </td>
                    <td
                      className="align-middle"
                      style={{
                        color: theme.primary,
                        fontWeight: 600,
                      }}
                    >
                      {Math.max(0, t.fields_count - 1)}
                    </td>
                    <td
                      style={{
                        ...actionsColStyle,
                        textAlign: "right",
                      }}
                    >
                      <i
                        className="bi bi-arrow-right-circle"
                        title="View"
                        style={{
                          cursor: "pointer",
                          color: theme.primary,
                          fontSize: 19,
                          padding: 5,
                          borderRadius: 8,
                          transition: "background .15s",
                        }}
                        onClick={() => handleRowAction("view", t)}
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {!error && (
        <Paginator
          page={page}
          pageCount={pageCount}
          onPrevious={handlePrevious}
          onNext={handleNext}
          onPage={handlePageChange}
          maxPagesToShow={3}
        />
      )}
    </div>
  );
}